import React from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import {
  CheckIcon,
  GlobeIcon,
  ImageIcon,
  Trash2Icon,
  VideoIcon,
} from "lucide-react";

import { OpenAPI } from "simplydo/interfaces";
import { VideoPlayer } from "simplydo/react/components/VideoPlayer";
import { styled } from "simplydo/react/styled";
import {
  FacebookIcon,
  GitHubIcon,
  InstagramIcon,
  LinkedInIcon,
} from "simplydo/react/ui/brand-icon";
import { XIcon } from "simplydo/react/ui/brand-icon";
import { Button } from "simplydo/react/ui/button";
import { Checkbox } from "simplydo/react/ui/checkbox";
import { Container } from "simplydo/react/ui/container";
import { Divider } from "simplydo/react/ui/divider";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H3, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Message } from "simplydo/react/ui/message";
import { Table, TableBody, TableCell, TableRow } from "simplydo/react/ui/table";
import { TextArea } from "simplydo/react/ui/textarea";

import constants from "utils/constants";
import { type UserMe, useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import { ImageChooser, VideoChooser } from "components/lib/Choosers";
import SettingActions from "components/lib/SettingActions";

import type { ProfileUpdate, ProfileUpload } from "../types";

const StyledAvatar = styled.div<{ $image: string }>`
  background-size: cover;
  background-position: center center;
  background-image: ${({ $image }) => ($image ? `url(${$image})` : "none")};
  height: 200px;
  width: 200px;
  display: block;
  margin: 10px auto 40px auto;
  border: 5px solid white;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.3);
  border-radius: 50%;
`;

const StyledDefaultAvatar = styled.div`
  display: inline-block;
  img {
    cursor: pointer;
    opacity: 0.8;
    transition: 0.3s opacity;
    width: 40px;
    margin-right: 10px;
    margin-bottom: 5px;
    border-radius: 50%;
    box-shadow: 2px 2px 4px rgba(50, 50, 50, 0.4);
    &:hover {
      opacity: 1;
    }
  }
`;

const StyledCover = styled.div<{ $image: string }>`
  height: 200px;
  width: 100%;
  background-size: cover;
  background-position: center center;
  background-image: ${({ $image }) => ($image ? `url(${$image})` : "none")};
  margin: 10px auto 40px auto;
  border: 5px solid white;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.3);
`;

type UserSettingsProfileProps = {
  profile: UserMe["profile"];
  updateProfile: (data: ProfileUpdate) => void;
  toggleTag: (tag: OpenAPI.Schemas["Tag"], checked: boolean) => void;
  saveProfile: () => void;
  saving: boolean;
};

const UserSettingsProfile = ({
  profile,
  updateProfile,
  toggleTag,
  saveProfile,
  saving,
}: UserSettingsProfileProps) => {
  const user = useCurrentUser();
  const { t } = useTranslation();

  if (!user || !profile) return null;

  const removeUpload = (i: number) => {
    const uploads = Object.assign([], profile.uploads);
    uploads.splice(i, 1);
    updateProfile({ uploads });
  };

  return (
    <Container>
      <H3>{t("users.edit.name.title")}</H3>
      {user?.ssoUser ? (
        <Message>
          Your avatar was automatically set from your single sign-on account.
        </Message>
      ) : null}
      <Grid columns={2} mobile={1}>
        <GridColumn>
          <FormField disabled={user?.ssoUser}>
            <FormLabel>{t("users.edit.name.firstName")}</FormLabel>
            <Input
              fluid
              defaultValue={profile.firstName}
              placeholder={t("users.edit.name.firstName")}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateProfile({ firstName: e.target.value })
              }
              autoComplete="given-name"
            />
          </FormField>
        </GridColumn>
        <GridColumn>
          <FormField disabled={user?.ssoUser}>
            <FormLabel>{t("users.edit.name.lastName")}</FormLabel>
            <Input
              fluid
              defaultValue={profile.lastName}
              placeholder={t("users.edit.name.lastName")}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateProfile({ lastName: e.target.value })
              }
              autoComplete="family-name"
            />
          </FormField>
        </GridColumn>
      </Grid>
      <Divider section />

      <H3>{t("users.edit.avatar.title")}</H3>
      <p>
        {profile.avatarWasSetByExternalSource ? (
          <Message>
            Your avatar was automatically set from your single sign-on account.
          </Message>
        ) : (
          t("users.edit.avatar.info")
        )}
      </p>
      <div>
        <StyledAvatar
          $image={util.mixinCssUrlFallback(
            util.avatarUrl({ ...user, profile }, "500x500"),
            util.avatarUrl(),
          )}
        />
        {!profile.avatarWasSetByExternalSource ? (
          <>
            <ImageChooser
              forType="user"
              forId={user._id}
              trigger={
                <Button color="secondary">
                  <Icon icon={ImageIcon} />
                  {t("users.edit.avatar.chooseImage")}
                </Button>
              }
              onComplete={(f, _n, u) => {
                updateProfile({
                  avatar: f,
                  avatarUrl: u,
                  avatarThumbnailUrls: {},
                });
              }}
            />
            <p style={{ marginTop: 15 }}>{t("users.edit.avatar.default")}</p>
            <div>
              {constants.avatars.map((avatar, i) => (
                <StyledDefaultAvatar
                  key={i}
                  onClick={() => {
                    updateProfile({
                      avatar: avatar,
                      avatarUrl: `https://cdn.simplydo.co.uk/images/avatars/${avatar}`,
                      avatarThumbnailUrls: {},
                    });
                  }}
                >
                  <img
                    alt="Default avatar"
                    src={`https://cdn.simplydo.co.uk/images/avatars/${avatar}`}
                  />
                </StyledDefaultAvatar>
              ))}
            </div>
          </>
        ) : null}
      </div>
      <Divider />

      <H3>
        {t("users.edit.coverImage.title")}
        <Subheader>{t("users.edit.coverImage.choose")}</Subheader>
      </H3>
      <div>
        {profile.coverImageUrl && (
          <StyledCover
            $image={util.getThumbnail(profile, "coverImage", "300x300")}
          />
        )}
        <ImageChooser
          forType="user"
          forId={user._id}
          trigger={
            <Button color="secondary">
              <Icon icon={ImageIcon} />
              {t("users.edit.coverImage.select")}
            </Button>
          }
          onComplete={(f, _n, u) => {
            updateProfile({
              coverImage: f,
              coverImageUrl: u,
              coverImageThumbnailUrls: {},
            });
          }}
        />
        {profile.coverImageUrl && (
          <Button
            size="small"
            onClick={() => {
              updateProfile({
                coverImage: "",
                coverImageUrl: undefined,
                coverImageThumbnailUrls: undefined,
              });
            }}
            style={{ marginLeft: 10 }}
            variant="outline"
          >
            <Icon icon={Trash2Icon} />
          </Button>
        )}
      </div>

      <Divider section />
      <H3>
        {t("users.edit.video.title")}
        <Subheader>{t("users.edit.video.info")}</Subheader>
      </H3>
      {profile.videoUrl && <VideoPlayer src={profile.videoUrl} />}
      <div style={{ marginTop: 20 }} />
      <div style={{ display: "flex" }}>
        <VideoChooser
          forType="user"
          forId={user._id}
          onComplete={(v, _n, u) => {
            updateProfile({ video: v, videoUrl: u });
          }}
          onError={toast.error}
          trigger={
            <Button size="small" color="secondary">
              <Icon icon={VideoIcon} />
              {t("users.edit.video.chooseVideo")}
            </Button>
          }
        />
        {profile.video ? (
          <Button
            size="small"
            onClick={() => {
              updateProfile({ video: "", videoUrl: undefined });
            }}
            style={{ marginLeft: 10 }}
            variant="outline"
          >
            <Icon icon={Trash2Icon} />
          </Button>
        ) : null}
      </div>

      <Divider />
      <H3>
        {t("generic.overview")}
        <Subheader>
          <p>{t("users.edit.overview.info")}</p>
          <p>
            <b>{t("users.edit.overview.tagline.title")}</b>
          </p>
        </Subheader>
      </H3>

      <FormField>
        <Input
          fluid
          defaultValue={profile.tagline}
          placeholder={t("users.edit.overview.tagline.placeholder")}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            updateProfile({ tagline: e.target.value })
          }
        />
      </FormField>
      <Divider hidden />
      <p>
        <b>{t("users.edit.overview.professionalProfile.title")}</b>
      </p>
      <TextArea
        defaultValue={profile.overview}
        style={{ minHeight: 200 }}
        onChange={(e) => updateProfile({ overview: e.target.value })}
      />

      <Divider />
      <Grid columns={2}>
        <GridColumn>
          <H3>{t("users.edit.overview.professionalPortfolio.title")}</H3>
          <FormField>
            <Input
              fluid
              icon={<LinkedInIcon />}
              iconPosition="left"
              defaultValue={profile.linkedin}
              placeholder={t(
                "users.edit.overview.professionalPortfolio.linkedIn",
              )}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateProfile({ linkedin: e.target.value })
              }
            />
          </FormField>
          <FormField>
            <Input
              fluid
              icon={<GitHubIcon />}
              iconPosition="left"
              defaultValue={profile.github}
              placeholder={t(
                "users.edit.overview.professionalPortfolio.github",
              )}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateProfile({ github: e.target.value })
              }
            />
          </FormField>
          <FormField>
            <Input
              fluid
              icon={<InstagramIcon />}
              iconPosition="left"
              defaultValue={profile.instagram}
              placeholder={t(
                "users.edit.overview.professionalPortfolio.instagram",
              )}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateProfile({ instagram: e.target.value })
              }
            />
          </FormField>
        </GridColumn>
        <GridColumn>
          <H3>{t("users.edit.overview.social.title")}</H3>
          <FormField>
            <Input
              fluid
              icon={<Icon icon={GlobeIcon} />}
              iconPosition="left"
              defaultValue={profile.website}
              placeholder="https://www.example.com"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateProfile({ website: e.target.value })
              }
            />
          </FormField>
          <FormField>
            <Input
              fluid
              icon={<Icon icon={XIcon} />}
              iconPosition="left"
              defaultValue={profile.twitter}
              placeholder={t("users.edit.overview.social.twitter")}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateProfile({ twitter: e.target.value })
              }
            />
          </FormField>
          <FormField>
            <Input
              fluid
              icon={<FacebookIcon />}
              iconPosition="left"
              defaultValue={profile.facebook}
              placeholder={t("users.edit.overview.social.facebook")}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateProfile({ facebook: e.target.value })
              }
            />
          </FormField>
        </GridColumn>
      </Grid>
      <span
        style={{
          display: "block",
          opacity: 0.6,
          fontStyle: "italic",
          marginTop: 10,
          fontSize: "0.85em",
        }}
      >
        For online accounts, please only include your account id, not the entire
        URL. For example, rather than{" "}
        <b>&quot;https://facebook.com/brianmcbrain&quot;</b>, just use{" "}
        <b>&quot;brianmcbrain&quot;</b>.
      </span>

      {profile.uploads && profile.uploads.length > 0 && (
        <React.Fragment>
          <Divider hidden />
          <H3>{t("users.edit.files.uploads")}</H3>
          <Table style={{ marginTop: 0 }} fixed>
            <TableBody>
              {profile.uploads.map((a, i) => {
                const upload = a as ProfileUpload;
                return (
                  <TableRow key={a.storedName}>
                    <TableCell>
                      <Icon icon={util.getFileIcon(a.storedName)} /> {a.title}
                    </TableCell>
                    <TableCell>
                      <a
                        target="_blank"
                        rel="noreferrer noopener"
                        href={upload.url}
                      >
                        {a.name}
                      </a>
                    </TableCell>
                    <TableCell textAlign="right">
                      <Button
                        size="small"
                        onClick={() => removeUpload(i)}
                        variant="outline"
                      >
                        <Icon icon={Trash2Icon} />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </React.Fragment>
      )}

      {(user.ownerOrganisation?.tagList?.filter(
        (tag) => !tag?.disabledEntities?.includes("users"),
      )?.length ?? 0) > 0 && (
        <>
          <Divider section />
          <H3>
            {t("tags.title")}
            <Subheader>{t("tags.selectTags")}</Subheader>
          </H3>
          <Grid columns={16}>
            {user.ownerOrganisation?.tagList
              ?.filter((tag) => !tag?.disabledEntities?.includes("users"))
              .map((tag) => (
                <GridColumn span={8} mobile={8} computer={5} key={tag.value}>
                  <Checkbox
                    toggle
                    checked={(user.tags || []).indexOf(tag._id) > -1}
                    onChange={(_e, c) => toggleTag(tag, c.checked as boolean)}
                  >
                    {tag.value}
                  </Checkbox>
                </GridColumn>
              ))}
          </Grid>
        </>
      )}

      <Divider section hidden />
      <SettingActions>
        <Button onClick={saveProfile} loading={saving} color="primary">
          <Icon icon={CheckIcon} />
          {t("generic.saveChanges")}
        </Button>
      </SettingActions>
    </Container>
  );
};

export default UserSettingsProfile;
